import { useMemo } from 'react';

import type { Field } from 'models/sitecore';
import type { Branded } from 'types';
import { is } from 'utils/helpers';

import { useLatest } from './useLatest';

export type FieldKey = Branded<string, 'FieldKey'>;

// Add FieldKey as a possible value type for every Field or value, since the
// proxy may return object keys.
type UnwrapField<T> = T extends Field<infer V> ? V : T;
type WithFieldKeyFields<T extends object> = {
	[K in keyof T]-?: Field<
		Exclude<UnwrapField<T[K]>, null | undefined> | FieldKey
	>;
};
type WithFieldKeyValues<T extends object> = {
	[K in keyof T]-?: Exclude<UnwrapField<T[K]>, null | undefined> | FieldKey;
};

function isFieldWithValue(field: unknown): field is Field<unknown> {
	return (
		is.object(field) &&
		'value' in field &&
		(is.truthyOrZero(field.value) || field.value === false)
	);
}

function isPlainValue(field: unknown) {
	return (
		!isFieldWithValue(field) &&
		!is.object(field) &&
		(is.truthyOrZero(field) || field === false)
	);
}

// Should restrict T to an object with `Field` values, but Record requires
// index signatures which doesn't work with interfaces.
// https://github.com/microsoft/TypeScript/issues/15300

/**
 * Get an object with Sitecore Fields that fall back to the property key
 * name if there is no value set.
 *
 * NOTE! Any non-Field values will be treated as a Field.
 *
 * @example
 *
 * interface Props {
 *   heading?: Field<string>;
 *   id?: number;
 * }
 * function Component(props: Props) {
 *   const { heading, id } = useSafeFields(props);
 *   return (
 *     <>
 *       Will render the heading value if set, otherwise 'heading'.
 *       <h1>{heading.value}</h1>
 *       This prop wasn't a Field but will behave like one, same as heading.
 *       It can also possibly be a string now.
 *       <p>{id.value}</p>
 *     </>
 *   );
 * }
 */
export function useSafeFields<T extends object>(fields: T | null | undefined) {
	// Keep fields in a ref to avoid creating a new proxy on every render.
	const fieldsRef = useLatest(fields);
	const proxy = useMemo(
		() =>
			new Proxy<T>(
				// The actual object is irrelevant, the getter handles everything.
				{} as T,
				{
					get(target, key): Field<unknown> {
						const field = fieldsRef.current?.[key as keyof T];
						return isFieldWithValue(field)
							? field
							: { value: isPlainValue(field) ? field : key };
					},
				},
			),
		[fieldsRef],
	);

	return proxy as WithFieldKeyFields<T>;
}

/**
 * Get an object with Sitecore Field values that fall back to the property key
 * name if there is no value set.
 *
 * @example
 *
 * interface Props {
 *   heading?: Field<string>;
 *   id?: number;
 * }
 * function Component(props: Props) {
 *   const { heading, id } = useSafeFieldValues(props);
 *   return (
 *     <>
 *       Will render the heading value if set, otherwise 'heading'.
 *       <h1>{heading}</h1>
 *       Will work as normal, but can possibly be a string now.
 *       <p>{id}</p>
 *     </>
 *   );
 * }
 */
export function useSafeFieldValues<T extends object>(
	fields: T | null | undefined,
) {
	// Keep fields in a ref to avoid creating a new proxy on every render.
	const fieldsRef = useLatest(fields);
	const proxy = useMemo(
		() =>
			new Proxy<T>(
				// The actual object is irrelevant, the getter handles everything.
				{} as T,
				{
					get(target, key) {
						const field = fieldsRef.current?.[key as keyof T];
						return isFieldWithValue(field)
							? field.value
							: isPlainValue(field)
								? field
								: key;
					},
				},
			),
		[fieldsRef],
	);

	return proxy as WithFieldKeyValues<T>;
}
